import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
} from "react";
import { message } from "antd";
import {
  CheckOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { API } from "../../EndpPoints";
import {
  authenticatedFetch,
  buildDownloadPurchaseContext,
  persistSuccessfulPayment,
} from "./paymentAccess";
import useSubscriptionStatus from "../Subscription/useSubscriptionStatus";

const PaymentStatus = ({
  setPaymentOpen,
  onPaymentSuccess,
  downloadLabel = "PDF",
  purchaseContext = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { waitForActiveSubscription } = useSubscriptionStatus();

  const resolvedPurchaseContext = useMemo(
    () =>
      buildDownloadPurchaseContext({
        ...(purchaseContext || {}),
        downloadLabel,
      }),
    [downloadLabel, purchaseContext],
  );

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleAuthorizedDownload = useCallback(
    async (source = "paid") => {
      if (downloading) return;

      setDownloading(true);

      try {
        const result = await onPaymentSuccess?.({
          paymentContext: resolvedPurchaseContext,
          source,
        });

        if (result?.ok === false) {
          const failureMessage =
            result?.reason === "server"
              ? "Download failed due to a server issue. Please try again."
              : result?.message || "Download failed. Please try again.";

          messageApi.error(failureMessage);

          console.error("Paid download attempt failed.", {
            result,
            source,
            purchaseContext: resolvedPurchaseContext,
          });
          return;
        }

        setPaymentOpen(false);
      } catch (error) {
        const failureMessage =
          "Download failed due to a server issue. Please try again.";

        messageApi.error(failureMessage);

        console.error("Paid download attempt crashed.", {
          error,
          source,
          purchaseContext: resolvedPurchaseContext,
        });
      } finally {
        setDownloading(false);
      }
    },
    [
      downloading,
      messageApi,
      onPaymentSuccess,
      resolvedPurchaseContext,
      setPaymentOpen,
    ],
  );

  const startPayment = useCallback(async () => {
    try {
      setLoading(true);

      const sdkLoaded = await loadRazorpay();
      if (!sdkLoaded) {
        messageApi.error("Failed to load Razorpay SDK");
        return;
      }

      const res = await authenticatedFetch(API.START_PAYMENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(resolvedPurchaseContext),
      });

      const data = await res.json();

      if (!data.order || !data.key) {
        messageApi.error("Payment initialization failed");
        return;
      }

      const options = {
        key: data.key,
        amount: data.order.amount,
        currency: "INR",
        name: "ResumeIQ",
        description: "ResumeIQ 7-Day Premium Subscription",
        order_id: data.order.id,
        method: {
          upi: true,
          card: false,
          netbanking: false,
          wallet: false,
          emi: false,
          paylater: false,
        },
        config: {
          display: {
            blocks: {
              upi: {
                name: "Pay Using UPI",
                instruments: [
                  {
                    method: "upi",
                  },
                ],
              },
            },
            sequence: ["block.upi"],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
        notes: {
          paymentMethod: "UPI_ONLY",
          plan: "resumeiq-7-day-premium",
        },
        theme: {
          color: "#0ea5e9",
        },
        handler: async (response) => {
          try {
            const verifyRes = await authenticatedFetch(API.VERIFY_PAYMENT, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ...response,
                ...resolvedPurchaseContext,
              }),
            });

            const verifyData = await verifyRes.json();

            if (verifyData.status === "success") {
              persistSuccessfulPayment(resolvedPurchaseContext, {
                paymentId: verifyData.transactionId,
                subscription: verifyData.subscription,
                raw: verifyData,
              });

              const activatedSubscription = await waitForActiveSubscription({
                initialSubscription: verifyData.subscription,
              });

              if (!activatedSubscription?.active) {
                messageApi.error(
                  "Your payment succeeded, but subscription activation is still syncing. Please try again in a moment.",
                );
                return;
              }

              messageApi.success("Your 7-day subscription is active.");
              messageApi.success(`Preparing ${downloadLabel}, please wait...`);
              await handleAuthorizedDownload("fresh-payment");
            } else {
              messageApi.error("Payment verification failed");
            }
          } catch (error) {
            console.error("Payment verification error.", {
              error,
              purchaseContext: resolvedPurchaseContext,
            });
            messageApi.error("Verification error");
          }
        },
      };

      const razor = new window.Razorpay(options);

      razor.on("payment.failed", (response) => {
        console.error("Payment failed.", {
          response: response?.error,
          purchaseContext: resolvedPurchaseContext,
        });
        messageApi.error("Payment Failed");
      });

      razor.open();
    } catch (error) {
      console.error("Payment start failed.", {
        error,
        purchaseContext: resolvedPurchaseContext,
      });
      messageApi.error("Payment start failed");
    } finally {
      setLoading(false);
    }
  }, [
    downloadLabel,
    handleAuthorizedDownload,
    messageApi,
    resolvedPurchaseContext,
    waitForActiveSubscription,
  ]);

  const primaryButtonLabel = downloading
    ? `Preparing ${downloadLabel}...`
      : loading
      ? "Opening UPI..."
      : `Unlock & download ${downloadLabel}`;

  return (
    <Fragment>
      {contextHolder}

      <div className="payment-wrapper">
        <div className="payment-hero">
          <div className="payment-plan-copy">
            <div className="payment-eyebrow">
              <ThunderboltOutlined />
              ResumeIQ Premium
            </div>
            <h2>Export your best resume</h2>
            <p>
              Activate seven days of unlimited downloads. Your current export
              continues automatically after payment.
            </p>
          </div>
          <div className="payment-price" aria-label="₹100 for seven days">
            <span className="payment-price-value">₹100</span>
            <span className="payment-price-term">7 days</span>
          </div>
        </div>

        <div className="payment-features" aria-label="Premium benefits">
          {[
            "Unlimited PDF downloads",
            "Unlimited DOCX exports",
            "Print-ready files",
            "All 19 templates included",
          ].map((feature) => (
            <div className="payment-feature" key={feature}>
              <span className="payment-feature-icon">
                <CheckOutlined />
              </span>
              {feature}
            </div>
          ))}
        </div>

        <button
          type="button"
          className="pay-btn"
          onClick={startPayment}
          disabled={loading || downloading}
        >
          {primaryButtonLabel}
        </button>

        <div className="payment-assurance">
          <span>
            <SafetyCertificateOutlined />
            Secure UPI payment via Razorpay
          </span>
          <span>No auto-renewal</span>
        </div>

        <style>{`
.payment-wrapper{
  width:100%;
  font-family:Inter,system-ui,sans-serif;
  color:#172033;
}

.payment-hero{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:24px;
  padding-bottom:24px;
  border-bottom:1px solid #e7ebf0;
}

.payment-plan-copy{
  max-width:390px;
}

.payment-eyebrow{
  display:flex;
  align-items:center;
  gap:7px;
  color:#087f68;
  font-size:12px;
  font-weight:700;
  text-transform:uppercase;
  letter-spacing:0;
  margin-bottom:12px;
}

.payment-plan-copy h2{
  margin:0 0 10px;
  color:#111827;
  font-size:28px;
  line-height:1.18;
  letter-spacing:0;
}

.payment-plan-copy p{
  margin:0;
  color:#5d6879;
  font-size:14px;
  line-height:1.6;
}

.payment-price{
  min-width:112px;
  padding:14px 16px;
  border:1px solid #b9ded6;
  border-radius:8px;
  background:#eef9f6;
  text-align:right;
}

.payment-price-value,
.payment-price-term{
  display:block;
}

.payment-price-value{
  color:#075e54;
  font-size:30px;
  font-weight:750;
  line-height:1;
}

.payment-price-term{
  margin-top:6px;
  color:#54716b;
  font-size:12px;
  font-weight:600;
}

.payment-features{
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:12px 20px;
  padding:24px 0;
}

.payment-feature{
  display:flex;
  align-items:center;
  gap:9px;
  min-width:0;
  color:#334155;
  font-size:14px;
  font-weight:550;
}

.payment-feature-icon{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:22px;
  height:22px;
  flex:0 0 22px;
  border-radius:50%;
  background:#dcf5ee;
  color:#087f68;
  font-size:11px;
}

.pay-btn{
  width:100%;
  min-height:50px;
  background:#1769e0;
  border:none;
  color:white;
  font-size:16px;
  font-weight:700;
  padding:14px 20px;
  border-radius:8px;
  cursor:pointer;
  transition:background .18s ease,box-shadow .18s ease,transform .18s ease;
  box-shadow:0 8px 20px rgba(23,105,224,0.22);
}

.pay-btn:hover{
  transform:translateY(-2px);
  background:#125abf;
  box-shadow:0 12px 26px rgba(23,105,224,0.28);
}

.pay-btn:disabled{
  opacity:0.65;
  cursor:not-allowed;
  transform:none;
}

.payment-assurance{
  display:flex;
  justify-content:center;
  flex-wrap:wrap;
  gap:8px 18px;
  margin-top:14px;
  color:#6b7280;
  font-size:12px;
}

.payment-assurance span{
  display:inline-flex;
  align-items:center;
  gap:6px;
}

@media (min-width:701px){
  .payment-hero{
    padding-right:28px;
  }
}

@media (max-width:700px){
  .payment-hero{
    gap:14px;
  }

  .payment-plan-copy h2{
    font-size:24px;
  }

  .payment-price{
    min-width:92px;
    padding:12px;
  }

  .payment-price-value{
    font-size:26px;
  }

  .payment-features{
    grid-template-columns:1fr;
    gap:11px;
    padding:20px 0;
  }
}

@media (max-width:430px){
  .payment-hero{
    flex-direction:column;
  }

  .payment-price{
    display:flex;
    align-items:baseline;
    gap:8px;
    width:100%;
    text-align:left;
  }
}
        `}</style>
      </div>
    </Fragment>
  );
};

export default PaymentStatus;
